import React, { useState, useEffect } from "react";
import { Card, CardBody, Row, Col, Input, Button } from "reactstrap";
import Select from "react-select";
import Handler from "../../Helpers/Handler";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { reloadWindowToPath } from "../../Helpers/Utils";

const CreateProduct = () => {

    /* ================= HELPERS ================= */
    const isEmpty = (v) => v === "" || v === null || v === undefined;

    /* ================= STATES ================= */
    const [form, setForm] = useState({
        name: "", sku: "", dealer: "", brand: "", description: "", 
        price: "", purchase_price: "", gst_rate: "", stock: "0", 
        unit: null, quantity: "", low_stock_limit: "", 
        discount_type: { value: "percentage", label: "Percentage" }, 
        discount_value: "0", category: null, sub_category: null
    });

    const [variants, setVariants] = useState([{
        name: "", price: "", purchase_price: "", discount_type: { value: "percentage", label: "Percentage" },
        discount_value: "0", gst_rate: "", quantity: "", low_stock_limit: "", sku: "", description: ""
    }]);

    const [categories, setCategories] = useState([]);
    const [subCategories, setSubCategories] = useState([]);

    const user = useSelector(s => s.customer.customer);
    const loading = useSelector(state => state.loading.loading);

    const discountTypes = [
        { value: "flat", label: "Flat" },
        { value: "percentage", label: "Percentage" }
    ];

    const unitOptions = [
        { value: "service", label: "Service" },
        { value: "package", label: "Package" },
        { value: "days", label: "Days" },
        { value: "month", label: "Month" },
        { value: "pcs", label: "Pieces (pcs)" }
    ];

    /* ================= SKU GENERATOR ================= */
    const generateSKU = (name) => {
        const base = name ? name.replace(/\s+/g, "").substring(0, 3).toUpperCase() : "SRV";
        return `${base}-${Math.floor(1000 + Math.random() * 9000)}`;
    };

    useEffect(() => {
        if (form.name && !form.sku) setForm(p => ({ ...p, sku: generateSKU(p.name) }));
    }, [form.name]);

    /* ================= LOAD DATA ================= */
    useEffect(() => { loadCategories(); }, []);

    const loadCategories = async () => {
        const res = await Handler({ method: "get", url: "/categories/list.php?limit=1000" });
        if (res?.success) {
            setCategories(res.data.categories.map(c => ({ value: c.id, label: c.name })));
        }
    };

    const loadSubCategories = async (id) => {
        const res = await Handler({ method: "get", url: `/sub_categories/list.php?category_id=${id}&limit=1000` });
        setSubCategories(res?.success ? res.data.sub_categories.map(sc => ({ value: sc.id, label: sc.name })) : []);
    };

    /* ================= VARIANTS ================= */
    const updateVariant = (i, k, v) => {
        const copy = [...variants];
        copy[i][k] = v;
        setVariants(copy);
    };

    const addVariant = () => {
        setVariants([...variants, {
            name: "", price: "", purchase_price: "", discount_type: { value: "percentage", label: "Percentage" },
            discount_value: "0", gst_rate: "", quantity: "", low_stock_limit: "", sku: "", description: ""
        }]);
    };

    /* ================= SUBMIT ================= */
    const submit = async () => {

        // Strict Validations for Digital Marketing
        if (!form.name.trim()) return toast.error("Service Name required");
        if (!form.category) return toast.error("Category required");
        if (isEmpty(form.price)) return toast.error("Price required");

        const payload = {
            name: form.name,
            price: Number(form.price),
            purchase_price: Number(form.purchase_price || 0),
            discount_type: form.discount_type?.value || "percentage",
            discount_value: Number(form.discount_value || 0),
            gst_rate: Number(form.gst_rate || 0),
            outlet_id: user.outlet_id,
            category_id: form.category.value,
            sub_category_id: form.sub_category?.value || "",
            dealer: form.dealer || "Local",
            brand: form.brand || "In-House",
            sku: form.sku || generateSKU(form.name),
            size: form.unit ? `${form.quantity || 1} ${form.unit.value}` : "1 Service",
            description: form.description || "",
            quantity: Number(form.stock || 0),
            low_stock_limit: Number(form.low_stock_limit || 0),
            variants: variants
                .filter(v => v.name.trim() !== "") // Only send variants that have a name
                .map(v => ({
                    name: v.name,
                    price: Number(v.price || form.price),
                    gst_rate: Number(v.gst_rate || form.gst_rate || 0),
                    quantity: Number(v.quantity || 1),
                    sku: v.sku || `${form.sku}-${v.name.substring(0,2).toUpperCase()}`,
                    description: v.description || form.description,
                    purchase_price: Number(v.purchase_price || 0),
                    discount_type: v.discount_type?.value || "percentage",
                    discount_value: Number(v.discount_value || 0),
                    low_stock_limit: Number(v.low_stock_limit || 0)
                }))
        };

        const res = await Handler({
            method: "post",
            url: "/products/create.php",
            data: payload
        });

        if (res?.success) {
            toast.success("Service added successfully");
            reloadWindowToPath('/products');
        } else {
            toast.error(res?.message || "Something went wrong");
        }
    };

    return (
        <div className="p-3">
            <Row className="mb-4">
                <Col><h4 className="fw-bold">Digital Marketing - Create Service</h4></Col>
            </Row>

            <Card className="mb-4 border-0 shadow-sm"><CardBody>
                <h6 className="mb-4 text-primary">Service Details</h6>
                <Row className="g-3 mb-4">
                    <Col md={6}>
                        <label>Service/Product Name *</label>
                        <Input placeholder="e.g. Social Media Ads" value={form.name}
                            onChange={e => setForm({ ...form, name: e.target.value })} />
                    </Col>
                    <Col md={6}>
                        <label>SKU (Custom/Auto)</label>
                        <Input placeholder="Auto generated" value={form.sku} 
                            onChange={e => setForm({ ...form, sku: e.target.value })} />
                    </Col>
                </Row>

                <Row className="g-3 mb-4">
                    <Col md={4}>
                        <label>Selling Price *</label>
                        <Input type="number" placeholder="0.00" value={form.price}
                            onChange={e => setForm({ ...form, price: e.target.value })} />
                    </Col>
                    <Col md={4}>
                        <label>Purchase/Base Cost</label>
                        <Input type="number" placeholder="Optional" value={form.purchase_price}
                            onChange={e => setForm({ ...form, purchase_price: e.target.value })} />
                    </Col>
                    <Col md={4}>
                        <label>GST %</label>
                        <Input type="number" placeholder="e.g. 18" value={form.gst_rate}
                            onChange={e => setForm({ ...form, gst_rate: e.target.value })} />
                    </Col>
                </Row>

                <Row className="g-3 mb-4">
                    <Col md={4}>
                        <label>Category *</label>
                        <Select options={categories} value={form.category}
                            onChange={v => { setForm({ ...form, category: v, sub_category: null }); loadSubCategories(v.value); }} />
                    </Col>
                    <Col md={4}>
                        <label>Sub Category</label>
                        <Select options={subCategories} value={form.sub_category}
                            onChange={v => setForm({ ...form, sub_category: v })} />
                    </Col>
                    <Col md={4}>
                        <label>Brand/Provider</label>
                        <Input placeholder="e.g. Google, Meta" value={form.brand}
                            onChange={e => setForm({ ...form, brand: e.target.value })} />
                    </Col>
                </Row>

                <Row className="g-3 mb-4">
                    <Col md={3}>
                        <label>Unit</label>
                        <Select options={unitOptions} value={form.unit}
                            onChange={v => setForm({ ...form, unit: v })} />
                    </Col>
                    <Col md={3}>
                        <label>Duration/Qty</label>
                        <Input type="number" placeholder="e.g. 30" value={form.quantity}
                            onChange={e => setForm({ ...form, quantity: e.target.value })} />
                    </Col>
                    <Col md={3}>
                        <label>Discount Type</label>
                        <Select options={discountTypes} value={form.discount_type}
                            onChange={v => setForm({ ...form, discount_type: v })} />
                    </Col>
                    <Col md={3}>
                        <label>Discount Value</label>
                        <Input type="number" value={form.discount_value}
                            onChange={e => setForm({ ...form, discount_value: e.target.value })} />
                    </Col>
                </Row>

                <Row>
                    <Col>
                        <label>Service Description</label>
                        <Input type="textarea" rows="3" placeholder="Describe the marketing package..."
                            value={form.description}
                            onChange={e => setForm({ ...form, description: e.target.value })} />
                    </Col>
                </Row>
            </CardBody></Card>

            <Card className="mb-4 border-0 shadow-sm"><CardBody>
                <div className="d-flex justify-content-between mb-3">
                    <h6>Service Packages (Variants)</h6>
                    <Button size="sm" color="primary" outline onClick={addVariant}>+ Add Package</Button>
                </div>

                {variants.map((v, i) => (
                    <div key={i} className="border rounded p-3 mb-3 bg-light">
                        <Row className="g-3">
                            <Col md={3}>
                                <label>Package Name</label>
                                <Input placeholder="e.g. 30 Days Package" value={v.name}
                                    onChange={e => updateVariant(i, "name", e.target.value)} />
                            </Col>
                            <Col md={2}>
                                <label>Price</label>
                                <Input type="number" value={v.price}
                                    onChange={e => updateVariant(i, "price", e.target.value)} />
                            </Col>
                            <Col md={2}>
                                <label>GST %</label>
                                <Input type="number" value={v.gst_rate}
                                    onChange={e => updateVariant(i, "gst_rate", e.target.value)} />
                            </Col>
                            <Col md={5}>
                                <label>Brief Description</label>
                                <Input placeholder="Specifics for this package" value={v.description}
                                    onChange={e => updateVariant(i, "description", e.target.value)} />
                            </Col>
                        </Row>
                    </div>
                ))}
            </CardBody></Card>

            <div className="text-end mb-5">
                <Button color="success" size="lg" disabled={loading} onClick={submit}>
                    {loading ? "Saving..." : "Create Marketing Service"}
                </Button>
            </div>
        </div>
    );
};

export default CreateProduct;